import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mic, Play, Pause, Loader2, Download, Volume2, Coins, Clock, User, Users, Crown, Languages, Waveform, Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PERSIST_KEY = 'creatorflow_voiceover';
const PERSIST_MINUTES = 5;

// Curated English Voices - Static list with REAL Voice IDs for generation
// Preview = Static MP3 file (NO API calls)
// Generation = Real ElevenLabs Voice ID
const CURATED_VOICES = {
  male: [
    { id: "JBFqnCBsd6RMkjVDRZzb", name: "Antoni", style: "Warm, Conversational", preview: "/voice-previews/male-1.mp3" },
    { id: "N2lVS1w4EtoT3dr4eOWO", name: "Arnold", style: "Deep, Authoritative", preview: "/voice-previews/male-2.mp3" },
    { id: "pNInz6obpgDQGcFmaJgB", name: "Adam", style: "Deep, Narration", preview: "/voice-previews/male-3.mp3" },
    { id: "nPczCjzI2devNBz1zQrb", name: "Josh", style: "Deep, Documentary", preview: "/voice-previews/male-4.mp3" },
    { id: "pqHfZKP75CvOlQylNhV4", name: "Sam", style: "Raspy, Authentic", preview: "/voice-previews/male-5.mp3" },
    { id: "SOYHLrjzK2X1ezoPC6cr", name: "Patrick", style: "Shouty, Energetic", preview: "/voice-previews/male-6.mp3" },
    { id: "IKne3meq5aSn9XLyUdCD", name: "James", style: "Australian, Calm", preview: "/voice-previews/male-7.mp3" },
    { id: "TX3LPaxmHKxFdv7VOQHJ", name: "Jeremy", style: "Excited, Energetic", preview: "/voice-previews/male-8.mp3" },
    { id: "CwhRBWXzGAHq8TQ4Fs17", name: "Michael", style: "Audiobook, Calm", preview: "/voice-previews/male-9.mp3" },
    { id: "bIHbv24MWmeRgasZH58o", name: "Ethan", style: "ASMR, Soft", preview: "/voice-previews/male-10.mp3" }
  ],
  female: [
    { id: "K7W7zLWeGoxU9YqWoB7A", name: "Rachel", style: "Warm, Storytelling", preview: "/voice-previews/female-1.mp3" },
    { id: "YcVw2QPIG0kz0qzrudfX", name: "Bella", style: "Soft, Friendly", preview: "/voice-previews/female-2.mp3" },
    { id: "tPzOTlbmuCEa6h67Xb6k", name: "Domi", style: "Strong, Confident", preview: "/voice-previews/female-3.mp3" },
    { id: "MiueK1FXuZTCItgbQwPu", name: "Elli", style: "Young, Emotional", preview: "/voice-previews/female-4.mp3" },
    { id: "oVXQ3H21hRI9OtM4YH5K", name: "Freya", style: "Overhyped, Cheerful", preview: "/voice-previews/female-5.mp3" },
    { id: "09vslFmztgVtc8A1kDJl", name: "Grace", style: "Southern US, Sweet", preview: "/voice-previews/female-6.mp3" },
    { id: "GSq0NPB1I3yKJdQ0rvzP", name: "Charlotte", style: "Swedish, Seductive", preview: "/voice-previews/female-7.mp3" },
    { id: "VhxAIIZM8IRmnl5fyeyk", name: "Lily", style: "British, Warm", preview: "/voice-previews/female-8.mp3" },
    { id: "sssn4wp3AspuK2kvy3Ym", name: "Glinda", style: "Witch, Expressive", preview: "/voice-previews/female-9.mp3" },
    { id: "kIC4kfVqgGXGVwgAx81Z", name: "Gigi", style: "Childish, Cute", preview: "/voice-previews/female-10.mp3" }
  ]
};

const Voiceovers = () => {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(() => {
    try {
      const saved = localStorage.getItem(PERSIST_KEY);
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        if (Date.now() - timestamp < PERSIST_MINUTES * 60 * 1000) return data;
        localStorage.removeItem(PERSIST_KEY);
      }
    } catch (e) {}
    return null;
  });
  
  // Audio refs
  const resultAudioRef = useRef(null);
  const previewAudioRef = useRef(null); // Stores current preview Audio instance
  
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewingVoice, setPreviewingVoice] = useState(null);
  
  // Form state
  const [script, setScript] = useState('');
  const [selectedGender, setSelectedGender] = useState('female');
  const [selectedVoice, setSelectedVoice] = useState(CURATED_VOICES.female[0]);

  // Check plan access - Pro or Max only
  const hasAccess = user?.subscriptionPlan === 'pro' || user?.subscriptionPlan === 'max';

  // Persist result when it changes
  useEffect(() => {
    if (result) {
      localStorage.setItem(PERSIST_KEY, JSON.stringify({ data: result, timestamp: Date.now() }));
    }
  }, [result]);

  // Update selected voice when gender changes
  useEffect(() => {
    setSelectedVoice(CURATED_VOICES[selectedGender][0]);
  }, [selectedGender]);

  // Cleanup preview audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
    };
  }, []);

  // Calculate credit estimate based on script
  const creditEstimate = useMemo(() => {
    const wordCount = script.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount === 0) return { words: 0, duration: 0, credits: 0 };
    
    // 1 second ≈ 2 words
    const duration = Math.ceil(wordCount / 2);
    // 3 credits per 5 seconds
    const credits = Math.ceil(duration / 5) * 3;
    
    return { words: wordCount, duration, credits };
  }, [script]);

  // Get user's total available credits
  const totalCredits = (user?.credits || 0) + (user?.purchasedCredits || 0);
  const hasEnoughCredits = totalCredits >= creditEstimate.credits;

  // Handle voice preview - Static MP3 playback, NO API calls, NO credits
  const handlePreview = (voice) => {
    // If clicking the same voice that's playing, stop it
    if (previewingVoice === voice.id) {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      setPreviewingVoice(null);
      return;
    }

    // Stop any currently playing preview first
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }

    // Create new Audio instance for the preview (static file, no API call)
    const audio = new Audio(voice.preview);
    previewAudioRef.current = audio;

    // Handle preview ended
    audio.onended = () => {
      setPreviewingVoice(null);
      previewAudioRef.current = null;
    };

    // Handle preview error (file not found)
    audio.onerror = () => {
      setPreviewingVoice(null);
      previewAudioRef.current = null;
      toast.error('Preview not available');
    };

    // Play the static preview file
    audio.play()
      .then(() => {
        setPreviewingVoice(voice.id);
      })
      .catch(() => {
        setPreviewingVoice(null);
        previewAudioRef.current = null;
        toast.error('Preview not available');
      });
  };

  // Handle generation
  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!script.trim()) {
      toast.error('Please enter a script');
      return;
    }

    if (!hasEnoughCredits) {
      toast.error(`Insufficient credits. You need ${creditEstimate.credits} credits but only have ${totalCredits}.`);
      return;
    }

    setLoading(true);
    setResult(null);
    
    try {
      const res = await api.post('/voiceover/generate', {
        script: script,
        voice: selectedVoice.id
      });
      
      setResult(res.data);
      toast.success(`Voiceover generated! (${res.data.creditsUsed} credits used)`);
      refreshUser();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to generate voiceover');
    } finally {
      setLoading(false);
    }
  };

  // Result audio controls
  const handlePlayPause = () => {
    if (!resultAudioRef.current) return;
    
    if (isPlaying) {
      resultAudioRef.current.pause();
    } else {
      resultAudioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!resultAudioRef.current) return;
    const pct = (resultAudioRef.current.currentTime / resultAudioRef.current.duration) * 100;
    setProgress(pct);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const handleDownload = () => {
    if (!result?.audioUrl) return;
    const link = document.createElement('a');
    link.href = result.audioUrl;
    link.download = `voiceover-${selectedVoice.name.toLowerCase()}.mp3`;
    link.click();
  };

  // Locked state for non-Pro/Max users
  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="voiceovers-locked">
        <div>
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              AI Voiceovers
            </h1>
            <div className="flex items-center gap-2 px-4 py-2 bg-violet-500 rounded-xl">
              <Crown className="w-5 h-5 text-white" />
              <span className="font-bold text-white">PRO</span>
            </div>
          </div>
          <p className="text-slate-400 mt-3 text-lg">Professional AI voice generation for your videos</p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center">
                <Mic className="w-8 h-8 text-violet-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Upgrade to Access AI Voiceovers</h2>
              <p className="text-slate-400 max-w-md mx-auto">
                AI Voiceovers require Pro Creator or Creator Max plan. Generate natural-sounding voiceovers powered by ElevenLabs.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Users className="w-4 h-4 text-violet-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">20+ Voice Options</p>
                  <p className="text-slate-500 text-xs">Male and female voices with unique styles</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Volume2 className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Natural Sounding</p>
                  <p className="text-slate-500 text-xs">ElevenLabs AI for lifelike audio</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Play className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Preview First</p>
                  <p className="text-slate-500 text-xs">Listen to voice samples before generating</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Download className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Download MP3</p>
                  <p className="text-slate-500 text-xs">Export audio files for any video editor</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button 
                onClick={() => navigate('/billing')}
                className="bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white"
                data-testid="voiceovers-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro Creator - $19/month
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentVoices = CURATED_VOICES[selectedGender];

  return (
    <div className="space-y-6" data-testid="voiceovers">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
          AI Voiceovers
        </h1>
        <p className="text-slate-400 text-lg">Generate professional voiceovers powered by ElevenLabs</p>
      </div>

      {/* Credit Info Banner */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-4 flex items-center gap-3">
        <Coins className="w-5 h-5 text-amber-400" />
        <div>
          <p className="text-white font-medium">Voiceovers cost 3 credits per 5 seconds</p>
          <p className="text-slate-400 text-sm">Preview voices for free. Credits are only deducted after successful generation.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Voice Selection */}
        <div className="space-y-4">
          {/* Gender Toggle */}
          <div className="flex gap-2">
            <Button
              onClick={() => setSelectedGender('female')}
              className={`flex-1 h-12 ${
                selectedGender === 'female'
                  ? 'bg-gradient-to-r from-pink-500 to-violet-500 text-white'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white'
              }`}
            >
              <Users className="w-4 h-4 mr-2" />
              Female Voices
            </Button>
            <Button
              onClick={() => setSelectedGender('male')}
              className={`flex-1 h-12 ${
                selectedGender === 'male'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white'
              }`}
            >
              <User className="w-4 h-4 mr-2" />
              Male Voices
            </Button>
          </div>

          {/* Voice Cards Grid */}
          <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-2">
            {currentVoices.map((voice) => (
              <Card
                key={voice.id}
                onClick={() => setSelectedVoice(voice)}
                className={`cursor-pointer transition-all ${
                  selectedVoice.id === voice.id
                    ? 'bg-gradient-to-br from-violet-500/20 to-cyan-500/20 border-violet-500/50 ring-2 ring-violet-500/30'
                    : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
                }`}
                data-testid={`voice-card-${voice.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-white">{voice.name}</h3>
                      <p className="text-xs text-slate-400">{voice.style}</p>
                    </div>
                    {selectedVoice.id === voice.id && (
                      <Badge className="bg-violet-500 text-white text-xs">Selected</Badge>
                    )}
                  </div>
                  
                  {/* Preview Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreview(voice);
                    }}
                    className={`w-full mt-2 ${
                      previewingVoice === voice.id
                        ? 'border-cyan-500 text-cyan-400 bg-cyan-500/10'
                        : 'border-slate-700 text-slate-400 hover:text-white'
                    }`}
                    data-testid={`preview-${voice.id}`}
                  >
                    {previewingVoice === voice.id ? (
                      <>
                        <Pause className="w-3 h-3 mr-1" />
                        Playing...
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-3 h-3 mr-1" />
                        Preview
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Right Column - Script & Generation */}
        <div className="space-y-4">
          {/* Selected Voice Display */}
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                selectedGender === 'female' 
                  ? 'bg-gradient-to-br from-pink-500/20 to-violet-500/20' 
                  : 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20'
              }`}>
                <Mic className={`w-6 h-6 ${selectedGender === 'female' ? 'text-pink-400' : 'text-cyan-400'}`} />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Selected Voice</p>
                <p className="text-white font-semibold">{selectedVoice.name}</p>
                <p className="text-slate-500 text-xs">{selectedVoice.style}</p>
              </div>
            </div>
          </div>

          {/* Script Input */}
          <Card className="bg-slate-800/30 border-slate-700/50">
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-white font-medium">Your Script</Label>
                <Textarea
                  data-testid="voiceover-script"
                  placeholder="Enter the text you want converted to speech..."
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 min-h-[180px] text-white placeholder:text-slate-500 resize-none"
                />
              </div>

              {/* Live Credit Estimate */}
              {script.trim() && (
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                    <Coins className="w-4 h-4 text-amber-400" />
                    Credit Estimate
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-white">{creditEstimate.words}</p>
                      <p className="text-xs text-slate-500">Words</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-cyan-400 flex items-center justify-center gap-1">
                        <Clock className="w-4 h-4" />
                        {creditEstimate.duration}s
                      </p>
                      <p className="text-xs text-slate-500">Est. Duration</p>
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${hasEnoughCredits ? 'text-emerald-400' : 'text-red-400'}`}>
                        {creditEstimate.credits}
                      </p>
                      <p className="text-xs text-slate-500">Credits</p>
                    </div>
                  </div>
                  
                  {!hasEnoughCredits && (
                    <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm text-center">
                        Insufficient credits. You have {totalCredits} credits.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={loading || !script.trim() || !hasEnoughCredits}
                className="w-full h-12 bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white font-medium"
                data-testid="generate-voiceover-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating with {selectedVoice.name}...
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    Generate Voiceover
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Generated Result */}
          {result && result.audioUrl && (
            <Card className="bg-slate-800/30 border-slate-700/50" data-testid="voiceover-result">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Play className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-semibold text-white">Generated Voiceover</h3>
                </div>

                {/* Hidden audio element */}
                <audio
                  ref={resultAudioRef}
                  src={result.audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleEnded}
                />

                <div className="flex items-center gap-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-12 h-12 rounded-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 shrink-0"
                    onClick={handlePlayPause}
                    data-testid="play-voiceover-btn"
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </Button>
                  <div className="flex-1">
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full transition-all duration-100"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-slate-700 text-slate-400 hover:text-white shrink-0"
                    onClick={handleDownload}
                    data-testid="download-voiceover-btn"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-slate-700 text-slate-300">
                      ~{result.duration}s
                    </Badge>
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                      {result.creditsUsed} credits used
                    </Badge>
                  </div>
                  <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                    Powered by ElevenLabs
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Voiceovers;
